import { getApi, postApi, putApi, delApi } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";
import {
  Integration,
  IntegrationFormData,
  IntegrationStatus,
} from "../types/integration";

/**
 * Normalize v2 integration template DTO to match legacy Integration shape.
 * v2 templates expose `active: boolean`; the web's `Integration` type still
 * declares `status: IntegrationStatus`, so we derive `status` from `active`
 * for compatibility. Other absent fields (e.g. `organization_id`) stay absent —
 * legacy also doesn't return them.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeTemplate(row: any): Integration {
  return {
    ...row,
    status: (row.active === false ? "inactive" : "active") as IntegrationStatus,
  } as Integration;
}

/**
 * Classe de serviço para operações relacionadas às integrações (templates/modelos)
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class IntegrationService {
  private static readonly COMPAT_BASE_URL = "/integrations";
  private static readonly BASE_URL = "/integrations";

  /**
   * Obtém todas as integrações disponíveis
   * @returns Lista de integrações
   */
  static async getAll(): Promise<Integration[]> {
    try {
      // Templates sao globais: nao ha filtro por organizacao.
      const url = `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}`;
      const rows = await getApi<Integration[]>(url);
      return rows.map(normalizeTemplate);
    } catch (error) {
      console.error("Erro ao buscar integrações:", error);
      throw error;
    }
  }

  /**
   * Obtém uma integração pelo ID
   * @param id ID da integração
   * @returns Dados da integração
   */
  static async getById(id: string): Promise<Integration> {
    try {
      const row = await getApi<Integration>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`,
      );
      return normalizeTemplate(row);
    } catch (error) {
      console.error(`Erro ao buscar integração com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Cria uma nova integração
   * @param data Dados da integração a ser criada
   * @returns Integração criada
   */
  static async create(data: {
    name: string;
    description: string;
    auth_fields: Record<string, string>;
    logo_url?: string;
    url: string;
    status?: IntegrationStatus;
  }): Promise<Integration> {
    try {
      // Templates sao globais (sem organization_id); `status` vira `active`.
      const { status, ...rest } = data;
      const body = {
        ...rest,
        ...(status !== undefined ? { active: status === "active" } : {}),
      };
      const row = await postApi<Integration>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}`,
        body,
      );
      return normalizeTemplate(row);
    } catch (error) {
      console.error("Erro ao criar integração:", error);
      throw error;
    }
  }

  /**
   * Atualiza uma integração existente
   * @param id ID da integração
   * @param data Dados a serem atualizados
   * @returns Integração atualizada
   */
  static async update(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      auth_fields: Record<string, string>;
      logo_url: string;
      url: string;
      status: IntegrationStatus;
    }>,
  ): Promise<Integration> {
    try {
      // v2 templates have `active: boolean`, not `status`
      const { status, ...rest } = data;
      const body = {
        ...rest,
        ...(status !== undefined ? { active: status === "active" } : {}),
      };
      const row = await putApi<Integration>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`,
        body,
      );
      return normalizeTemplate(row);
    } catch (error) {
      console.error(`Erro ao atualizar integração com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Atualiza especificamente os campos de autenticação da integração
   * @param id ID da integração
   * @param values Valores do formulário de autenticação
   * @returns Integração atualizada
   */
  static async updateAuthentication(
    id: string,
    values: IntegrationFormData,
  ): Promise<Integration> {
    return this.update(id, {
      auth_fields: values.auth_fields,
      status: "active" as IntegrationStatus,
    });
  }

  /**
   * Atualiza o status de uma integração
   * @param id ID da integração
   * @param status Novo status
   * @returns Integração atualizada
   */
  static async updateStatus(
    id: string,
    status: IntegrationStatus,
  ): Promise<Integration> {
    return this.update(id, { status });
  }

  /**
   * Remove uma integração
   * @param id ID da integração
   * @returns Verdadeiro se a remoção foi bem-sucedida
   */
  static async delete(id: string): Promise<boolean> {
    try {
      const url = `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`;
      await delApi(url);
      return true;
    } catch (error) {
      console.error(`Erro ao remover integração com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtém integrações ativas que não estejam na lista de IDs fornecida
   * @param selectedIntegrations Lista de IDs de integrações já selecionadas
   * @returns Lista de integrações disponíveis
   */
  static async getAvailableIntegrations(
    selectedIntegrations: string[] = [],
  ): Promise<Integration[]> {
    try {
      const integrations = await this.getAll();
      // normalizeTemplate already maps active -> status, so this filter
      // works the same in both legacy and v2 modes.
      return integrations.filter(
        (integration) =>
          integration.status === "active" &&
          !selectedIntegrations.includes(integration.id),
      );
    } catch (error) {
      console.error("Erro ao buscar integrações disponíveis:", error);
      throw error;
    }
  }

  /**
   * Obtém a contagem de integrações para um produto específico
   * @param workspaceId ID do produto
   * @returns Número de integrações
   */
  static async getCountByProduct(workspaceId: string): Promise<number> {
    try {
      const result = await getApi<{ count: number }>(
        `${this.COMPAT_BASE_URL}/count?product_id=${workspaceId}`,
      );
      return result.count;
    } catch (error) {
      console.error(
        `Erro ao obter contagem de integrações para o produto ${workspaceId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Busca integrações por uma lista de IDs
   * @param ids Lista de IDs de integrações
   * @returns Lista de integrações
   */
  static async getByIds(ids: string[]): Promise<Integration[]> {
    try {
      if (!ids.length) return [];
      // v2 aceita `?ids=a&ids=b` e o CSV `?ids=a,b`; aqui vai o CSV, montado por
      // URLSearchParams para o id ser escapado em vez de concatenado cru.
      const params = new URLSearchParams({ ids: ids.join(",") });
      const url = `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/multiple?${params.toString()}`;
      const rows = await getApi<Integration[]>(url);
      return rows.map(normalizeTemplate);
    } catch (error) {
      console.error("Erro ao buscar integrações por IDs:", error);
      throw error;
    }
  }

  /**
   * Obtém as integrações ativas da organização do token.
   * @returns Lista de integrações ativas
   */
  static async getActive(): Promise<Integration[]> {
    try {
      // A organização vem do token: não há filtro por organizationId.
      const rows = await getApi<Integration[]>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/active`,
      );
      return rows.map(normalizeTemplate);
    } catch (error) {
      console.error("Erro ao buscar integrações ativas:", error);
      throw error;
    }
  }

  /**
   * Obtém modelos de integração ativos ordenados por nome
   * @returns Lista de modelos de integração
   */
  static async getActiveModels(): Promise<Integration[]> {
    try {
      // v2 returns synthesized model DTOs directly (no envelope)
      const rows = await getApi<Integration[]>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/models`,
      );
      return rows;
    } catch (error) {
      console.error("Erro ao buscar modelos de integração:", error);
      throw error;
    }
  }
}
